// @generated-imports
import { armFocusForwarder, disarmFocusForwarder, focusEmulatorFrame, powerOffEmulator, showToast, updateExitRing } from './app.js';
import { dom, state } from './core.js';
import { announce, gpPickerHighlight, gpPickerMove, gpPickerOptions, gpShortName, setGamepadStatus } from './gamepad.js';
import { closeTopLayer } from './layers.js';
import { collectDroppedFiles, importFilesBatch, playLocalRom } from './library.js';
import { importStateFile, isStateFile } from './states.js';
import { analyzeZipRom, closeCorePicker, openCorePicker } from './zipdetect.js';
// @generated-imports
/* ===========================================================================
   ROM LOCAL VIA DRAG & DROP
   ---------------------------------------------------------------------------
   Permite arrastar um arquivo de ROM do PC para a janela. O core é deduzido
   pela extensão (mesma técnica do https://demo.emulatorjs.org/). Extensões
   ambíguas (.bin, .cue, .iso, .chd, .zip…) abrem um seletor de sistema.
   O arquivo (File) é entregue ao iframe do player via postMessage e o
   EmulatorJS o recebe diretamente em EJS_gameUrl — nada do fluxo de ROMs
   hospedadas foi alterado.
   ======================================================================== */

// Extensão → core (mapeamento direto, sem ambiguidade)
// Cobertura completa dos sistemas do EmulatorJS:
// https://emulatorjs.org/docs/systems
const LOCAL_EXTENSION_CORE_MAP = {
  // Nintendo
  nes: 'nes', fds: 'nes', unif: 'nes', unf: 'nes',
  smc: 'snes', fig: 'snes', sfc: 'snes', gd3: 'snes', gd7: 'snes',
  dx2: 'snes', bsx: 'snes', swc: 'snes',
  z64: 'n64', n64: 'n64', v64: 'n64', ndd: 'n64',
  gb: 'gb', gbc: 'gb', dmg: 'gb',
  gba: 'gba',
  nds: 'nds',
  vb: 'vb', vboy: 'vb',
  // Sega
  sms: 'segaMS',
  gg: 'segaGG',
  md: 'segaMD', gen: 'segaMD', smd: 'segaMD',
  '32x': 'sega32x',
  // Sony
  pbp: 'psx', mdf: 'psx', cbn: 'psx',
  cso: 'psp',
  // Atari
  a26: 'atari2600',
  a52: 'atari5200',
  a78: 'atari7800',
  lnx: 'lynx',
  jag: 'jaguar', j64: 'jaguar', abs: 'jaguar', cof: 'jaguar',
  // Commodore
  d64: 'vice_x64sc', t64: 'vice_x64sc', x64: 'vice_x64sc',
  g64: 'vice_x64sc', crt: 'vice_x64sc',
  // Cartuchos VIC-20 (blocos de memória)
  '20': 'vice_xvic', '40': 'vice_xvic', '60': 'vice_xvic',
  a0: 'vice_xvic', b0: 'vice_xvic',
  // Commodore Amiga (PUAE)
  adf: 'puae', adz: 'puae', dms: 'puae', fdi: 'puae',
  ipf: 'puae', hdf: 'puae', hdz: 'puae', uae: 'puae', lha: 'puae',
  // Outros
  pce: 'pce',
  ngp: 'ngp', ngc: 'ngp',
  ws: 'ws', wsc: 'ws',
  col: 'coleco', cv: 'coleco',
};

// Extensões que servem a mais de um sistema → pergunta ao usuário.
// A ordem das listas é a ordem dos botões no popup (mais provável primeiro).
const LOCAL_AMBIGUOUS_EXTENSIONS = {
  // Imagens de disco/cartucho genéricas
  bin:  ['segaMD', 'psx', 'segaCD', 'sega32x', 'segaMS', 'segaGG',
         'atari2600', 'atari5200', 'atari7800', 'coleco', 'jaguar',
         'vb', '3do', 'n64'],
  cue:  ['psx', 'segaCD', 'segaSaturn', '3do', 'pce'],
  iso:  ['psx', 'psp', 'segaCD', 'segaSaturn', '3do', 'puae'],
  chd:  ['psx', 'segaCD', 'segaSaturn', 'psp', '3do', 'pce'],
  img:  ['psx', 'segaCD', '3do'],
  ccd:  ['psx', 'segaSaturn'],
  toc:  ['psx', 'segaSaturn'],
  mds:  ['segaSaturn', 'psx'],
  m3u:  ['psx', 'segaSaturn', 'puae', 'vice_x64sc'],
  exe:  ['psx'],
  elf:  ['psp', 'psx'],
  o:    ['lynx'],
  rom:  ['coleco', 'jaguar', 'atari5200'],
  // Commodore: formatos compartilhados entre as máquinas da família
  prg:  ['vice_x64sc', 'vice_x128', 'vice_xvic', 'vice_xplus4', 'vice_xpet', 'jaguar'],
  p00:  ['vice_x64sc', 'vice_x128', 'vice_xvic', 'vice_xplus4', 'vice_xpet'],
  tap:  ['vice_x64sc', 'vice_x128', 'vice_xvic', 'vice_xplus4', 'vice_xpet'],
  d71:  ['vice_x128', 'vice_x64sc'],
  d81:  ['vice_x128', 'vice_x64sc'],
  // Pacotes: arcade usa o zip inteiro; consoles têm a ROM dentro
  zip:  ['arcade', 'mame2003', 'nes', 'snes', 'segaMD', 'gba', 'gb',
         'n64', 'psx', 'segaMS', 'segaGG', 'atari2600', 'coleco'],
  '7z': ['arcade', 'mame2003', 'nes', 'snes', 'segaMD', 'gba', 'gb',
         'n64', 'psx', 'segaMS', 'segaGG', 'atari2600', 'coleco'],
};

// Rótulos amigáveis para o seletor de core — todos os sistemas suportados.
const LOCAL_CORE_LABELS = {
  // Nintendo
  nes: 'NES / Famicom',
  snes: 'Super Nintendo (SNES)',
  n64: 'Nintendo 64',
  gb: 'Game Boy / Color',
  gba: 'Game Boy Advance',
  nds: 'Nintendo DS',
  vb: 'Virtual Boy',
  // Sega
  segaMS: 'Sega Master System',
  segaGG: 'Sega Game Gear',
  segaMD: 'Sega Mega Drive / Genesis',
  segaCD: 'Sega CD',
  sega32x: 'Sega 32X',
  segaSaturn: 'Sega Saturn',
  // Sony
  psx: 'PlayStation',
  psp: 'PlayStation Portable',
  // Arcade
  arcade: 'Arcade (FBNeo)',
  mame2003: 'Arcade (MAME 2003)',
  // Atari
  atari2600: 'Atari 2600',
  atari5200: 'Atari 5200',
  atari7800: 'Atari 7800',
  lynx: 'Atari Lynx',
  jaguar: 'Atari Jaguar',
  // Commodore
  vice_x64sc: 'Commodore 64',
  vice_x128: 'Commodore 128',
  vice_xvic: 'Commodore VIC-20',
  vice_xplus4: 'Commodore Plus/4',
  vice_xpet: 'Commodore PET',
  puae: 'Commodore Amiga',
  // Outros
  '3do': '3DO',
  pce: 'PC Engine / TurboGrafx-16',
  ngp: 'Neo Geo Pocket',
  ws: 'WonderSwan',
  coleco: 'ColecoVision',
};

function setupLocalRomDragAndDrop() {
  if (!dom.dropOverlay) return;

  let dragDepth = 0;

  const isFileDrag = (event) => {
    const types = event.dataTransfer && event.dataTransfer.types;
    return Boolean(types && Array.from(types).includes('Files'));
  };

  const showOverlay = () => dom.dropOverlay.classList.remove('is-hidden');
  const hideOverlay = () => {
    dragDepth = 0;
    dom.dropOverlay.classList.add('is-hidden');
  };

  // Fase de captura + preventDefault incondicional em dragover/drop:
  // é o que impede o Chrome de "navegar"/baixar o arquivo solto na página,
  // mesmo que algum elemento intermediário engula o evento.
  document.addEventListener('dragenter', (event) => {
    event.preventDefault();
    if (!isFileDrag(event)) return;
    dragDepth += 1;
    showOverlay();
  }, true);

  document.addEventListener('dragover', (event) => {
    // preventDefault é obrigatório para permitir o drop (e bloquear o download).
    event.preventDefault();
    if (isFileDrag(event)) {
      event.dataTransfer.dropEffect = 'copy';
      showOverlay();
    }
  }, true);

  document.addEventListener('dragleave', (event) => {
    if (!isFileDrag(event)) return;
    dragDepth = Math.max(0, dragDepth - 1);
    // relatedTarget === null => o cursor saiu da janela do navegador.
    if (dragDepth === 0 || event.relatedTarget === null) hideOverlay();
  }, true);

  document.addEventListener('dragend', hideOverlay, true);

  document.addEventListener('drop', (event) => {
    event.preventDefault();
    hideOverlay();
    if (!isFileDrag(event)) return;

    collectDroppedFiles(event.dataTransfer).then((dropped) => {
      // Arquivos de estado (.emutab-state.json) vindos de outro aparelho.
      dropped.filter(isStateFile).forEach((file) => importStateFile(file));
      const files = dropped.filter((file) => !isStateFile(file));
      if (!files.length) return;
      if (files.length === 1) handleDroppedRomFile(files[0]);
      else importFilesBatch(files); // pasta inteira ou vários arquivos
    });
  }, true);

  // Arquivos soltos DENTRO do iframe do emulador: os eventos de drag não
  // chegam à página pai, então o player.html captura o drop e repassa o
  // File por postMessage. Listener permanente, independente do jogo atual.
  window.addEventListener('message', (event) => {
    // Aceita apenas mensagens do iframe atual do player, na nossa origem e
    // da sessão corrente (cargas antigas não enxergam este listener).
    if (event.origin !== window.location.origin) return;
    const data = event.data || {};
    if (!state.currentIframe || event.source !== state.currentIframe.contentWindow) return;
    if (!state.session || data.session !== state.session.id) return;

    if (data.type === 'emutab:dropped-file' && data.file) {
      hideOverlay();
      handleDroppedRomFile(data.file);
      return;
    }

    // Jogo nunca configurado recebeu os controles padrão do sistema —
    // avisa uma vez por sessão para o usuário saber de onde veio o mapeamento.
    if (data.type === 'emutab:controls-seeded' && !state.controlsToastShown) {
      state.controlsToastShown = true;
      showToast('Controles padrão do sistema aplicados. Ajustes feitos aqui viram o novo padrão do console.');
      return;
    }

    // Gamepad dormiu/acordou: dá nome ao mistério do "jogo parou de
    // responder" e confirma a religação automática. Dedupe de 4s evita
    // spam quando o navegador dispara eventos em rajada.
    if (data.type === 'emutab:gamepad-connected' || data.type === 'emutab:gamepad-disconnected') {
      const now = Date.now();
      const signature = data.type + ':' + (data.id || '');
      if (state.lastGamepadToast === signature && now - state.lastGamepadToastAt < 4000) return;
      state.lastGamepadToast = signature;
      state.lastGamepadToastAt = now;

      const name = data.id || 'Controle';
      if (data.type === 'emutab:gamepad-connected') {
        // Devolve o foco ao iframe: o Chromium só entrega dados de botões
        // ao frame focado, e a reconexão costuma acontecer com o foco na
        // página pai (o usuário clicou fora / apertou Start).
        focusEmulatorFrame();
        setGamepadStatus('on', gpShortName(name));
        showToast(`🎮 ${name} conectado e religado ao jogo.`);
      } else {
        setGamepadStatus('off', gpShortName(name));
        showToast(`🎮 ${name} desconectou — se dormiu por inatividade, aperte Start para religar.`);
      }
      return;
    }

    // O navegador congelou os dados do gamepad neste iframe (focus-gating
    // do Chromium após dormir/acordar). Tentamos devolver o foco já; se o
    // congelamento persistir, o próximo clique em qualquer lugar da página
    // é encaminhado como foco para o iframe.
    if (data.type === 'emutab:gamepad-frozen') {
      focusEmulatorFrame();
      armFocusForwarder();
      setGamepadStatus('stalled', data.id ? gpShortName(data.id) : '');
      if (!state.frozenToastShown) {
        state.frozenToastShown = true;
        showToast(`🎮 O navegador pausou os dados do ${data.id || 'controle'} — clique na tela do jogo para reativar.`);
      }
      return;
    }

    if (data.type === 'emutab:gamepad-active') {
      state.frozenToastShown = false;
      disarmFocusForwarder();
      setGamepadStatus('on', '');
      return;
    }

    // Combo Select+Start (modo TV): anel de progresso enquanto segura,
    // "Desligar" ao completar.
    if (data.type === 'emutab:exit-progress') {
      updateExitRing(typeof data.progress === 'number' ? data.progress : 0);
      return;
    }
    if (data.type === 'emutab:exit-request') {
      updateExitRing(0);
      powerOffEmulator();
    }
  });

  dom.corePickerCancel.addEventListener('click', closeCorePicker);
  // Tab/Shift+Tab movem o foco DOM entre as opções: o realce do controle
  // acompanha — sem isto, o A do gamepad acionava a opção antiga realçada.
  dom.corePickerGrid.addEventListener('focusin', (event) => {
    const options = gpPickerOptions();
    const index = options.indexOf(event.target);
    if (index >= 0 && index !== state.gpPickerIndex) gpPickerHighlight(index);
  });
  // Setas do teclado navegam o seletor de core (Tab já funcionava; agora o
  // realce visual/lógico acompanha e Enter aciona a opção focada).
  dom.corePickerGrid.addEventListener('keydown', (event) => {
    const keyMap = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
    const direction = keyMap[event.key];
    if (!direction) return;
    event.preventDefault();
    gpPickerMove(direction);
  });
  dom.corePicker.addEventListener('click', (event) => {
    if (event.target === dom.corePicker) closeCorePicker();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeTopLayer(); // a camada do topo fecha; sem camada, nada acontece
  });
}

async function handleDroppedRomFile(file) {
  const extension = getFileExtension(file.name);

  if (!extension) {
    showToast('Não foi possível identificar a extensão do arquivo arrastado.');
    return;
  }

  let core = LOCAL_EXTENSION_CORE_MAP[extension] || null;
  let candidates = null;
  let pickerMessage = null;

  // .zip: espiamos o índice interno (central directory) sem descompactar.
  // Se houver uma ROM de extensão inequívoca lá dentro, o core é escolhido
  // sozinho; se for um dump de CD (.cue/.bin/.iso/.chd), o seletor mostra
  // apenas os sistemas de CD compatíveis; se parecer romset arcade
  // (muitos arquivos sem extensão reconhecida), Arcade vem primeiro.
  if (!core && extension === 'zip') {
    const analysis = await analyzeZipRom(file);
    if (analysis && analysis.core) {
      showToast(`Detectado ${LOCAL_CORE_LABELS[analysis.core] || analysis.core} dentro do .zip (${analysis.reason}).`);
      playLocalRom(file, analysis.core);
      return;
    }
    if (analysis && analysis.candidates && analysis.candidates.length) {
      candidates = analysis.candidates;
      pickerMessage = analysis.message || null;
    }
  }

  // .cue e .m3u são "índices" que referenciam outros arquivos (.bin, discos).
  // Soltos sozinhos, o EmulatorJS não encontra as faixas. Avisa, mas deixa
  // prosseguir — um .zip com tudo dentro funciona melhor nesses casos.
  if (extension === 'cue' || extension === 'm3u') {
    showToast(`Arquivos .${extension} referenciam outros arquivos. Prefira soltar o .bin/.chd diretamente, ou um .zip contendo o jogo completo.`);
  }

  if (!core) {
    if (!candidates) {
      candidates = LOCAL_AMBIGUOUS_EXTENSIONS[extension] || Object.keys(LOCAL_CORE_LABELS);
    }
    core = await openCorePicker(file.name, extension, candidates, pickerMessage);
    if (!core) {
      announce('Seleção de sistema cancelada.');
      return;
    }
  }

  playLocalRom(file, core);
}

function getFileExtension(name) {
  const match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
  return match ? match[1].toLowerCase() : '';
}

// @generated-exports
export { LOCAL_AMBIGUOUS_EXTENSIONS, LOCAL_CORE_LABELS, LOCAL_EXTENSION_CORE_MAP, getFileExtension, handleDroppedRomFile, setupLocalRomDragAndDrop };
// @generated-exports
